import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { IconNotification } from '../../../../shared/icon/icon'
import { getCapabilities, notifyAvailable, unreadCount } from '../../../../api-new/state/notify'
import { holdUnreadPoll, setUnread, setUnreadMeta, useUnread } from './unread-store'
import './notifications.scss'

// The service's suggested inbox poll (C-4 Capabilities.pollSeconds, 60),
// never faster than this: the badge is a background poll, not a live feed.
export const POLL_FLOOR_SECONDS = 30
const DEFAULT_POLL_SECONDS = 60

export const INBOX_PATH = '/org/notifications'

const badgeText = n => (n > 99 ? '99+' : String(n))

// The header's bell: a link to the inbox with the unread badge. It is the
// one place that polls the count (inbox/unread-count, edxr-bg=1, R-BG):
// once on mount, then every pollSeconds while the tab is visible, and at
// once when the tab shows again. The inbox page reads the same store and
// refetches its list only when latestId moves. A build without notify
// (no REACT_APP_API_NOTIFY) or a deployment without the inbox shows
// nothing; an unreachable service leaves the bell with no badge.
// The unread poll (one for the whole page, however many bells): the
// capabilities once, for pollSeconds and whether there is an inbox at all,
// then the count at once and every pollSeconds while the tab is visible,
// and at once when it shows again. Returns its stop.
const startPoll = () => {
  let live = true
  let timer = null
  let seconds = DEFAULT_POLL_SECONDS

  const read = async () => {
    try {
      const next = await unreadCount({ background: true })
      if (!live) return
      setUnread(next || { count: 0 })
      setUnreadMeta({ reachable: true })
    } catch (err) {
      if (live) setUnreadMeta({ reachable: false })
    }
  }
  const stop = () => {
    if (timer) clearInterval(timer)
    timer = null
  }
  const start = () => {
    stop()
    timer = setInterval(read, seconds * 1000)
  }
  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      read()
      start()
    } else {
      stop()
    }
  }

  ;(async () => {
    try {
      const caps = await getCapabilities({ background: true })
      if (!live) return
      if (caps && caps.inbox === false) {
        setUnreadMeta({ inbox: false })
        return
      }
      setUnreadMeta({ inbox: true })
      if (caps && Number(caps.pollSeconds) > 0) seconds = Math.max(POLL_FLOOR_SECONDS, Number(caps.pollSeconds))
    } catch (err) {
      // Capabilities unreachable: keep the default interval; the count's
      // own read says whether the service answers.
    }
    if (!live) return
    document.addEventListener('visibilitychange', onVisibility)
    if (document.visibilityState === 'visible') {
      await read()
      if (live) start()
    }
  })()

  return () => {
    live = false
    stop()
    document.removeEventListener('visibilitychange', onVisibility)
  }
}

// The header's bell: a link to the inbox with the unread badge (the
// unread count, inbox/unread-count with edxr-bg=1, R-BG). The inbox page
// reads the same store and refetches its list only when latestId moves. A
// build without notify (no REACT_APP_API_NOTIFY) or a deployment without
// the inbox shows nothing; an unreachable service leaves the bell with no
// badge.
export const NotificationBell = ({ className }) => {
  const available = notifyAvailable()
  const unread = useUnread()

  useEffect(() => (available ? holdUnreadPoll(startPoll) : undefined), [available])

  if (!available || !unread.inbox) return null

  const count = unread.reachable || unread.known ? unread.count : 0
  const label = count > 0 ? `Notifications, ${count} unread` : 'Notifications'

  return (
    <Link to={INBOX_PATH} className={`btn btn-light border mx-1 nt-bell ${className || ''}`.trim()} aria-label={label} title={label}>
      <IconNotification />
      {count > 0 && <span className="nt-bell-badge" data-testid="notify-unread-badge" aria-hidden="true">{badgeText(count)}</span>}
    </Link>
  )
}
